import { NavigationBar } from '@travelnest/hui';
import { Spacer } from '@travelnest/merlin.spacer';
import * as React from 'react';
import { useStore } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { IThunkDispatch } from 'store/middleware';
import { IState } from 'store/reducer';
import { currentUserId } from '../../../entities/auth/session/selectors';
import { HighBlockedDaysModal } from '../../components/high-blocked-days-modal/HighBlockedDaysModal';

import { useCustomerExperiments } from '../../../experiment';
import { InlineChurnAlert } from '../../components/churn-alert';
import { PropertySwitcher } from '../../components/property-switcher/PropertySwitcher';
import { CalendarBundle } from './Calendar';
import { useCustomersProperties } from './hooks';

export const calendarPrintingStyles = `
	@media print {
		body {
			color-adjust: exact !important;
			-webkit-print-color-adjust: exact !important;
		}

		#calendar-settings {
			visibility: hidden;
		}

		.calendar-column {
			width: 100%;
			height: 85vh;
		}

		.sidebar {
			visibility: hidden;
		}
	}
`;

const CalendarPage: React.FC = () => {
	const navigate = useNavigate();
	const { getState }: { getState: () => IState; dispatch: IThunkDispatch } =
		useStore();
	const customerId = currentUserId(getState());
	const persisted = getState().views.calendar.persistence;

	const { id: routePropertyId } = useParams();
	const [propertyId, setPropertyId] = React.useState<
		string | number | undefined
	>(routePropertyId || persisted?.propertyId || undefined);
	const showPropertySwitcher = routePropertyId === undefined;

	// This hook will not load data when the 2nd argument is false.
	const { properties, loading } = useCustomersProperties(
		customerId,
		showPropertySwitcher
	);
	const customerExperiments = useCustomerExperiments(customerId);
	const isUsingNewIcalWizard =
		customerExperiments?.['new-ical-flow'] === 'on';

	return (
		<div>
			<HighBlockedDaysModal propertyId={propertyId} />
			<NavigationBar
				title="Calendar"
				navigationMenuConfig={
					isUsingNewIcalWizard
						? {
								iconProps: {
									icon: 'Settings',
									id: 'settings-icon-button',
									'aria-label': 'Calendar settings',
									variant: 'secondary',
									fill: 'light',
									size: 'small',
								},
								menuItems: [
									{
										icon: 'CalendarPlus',
										label: 'Connect calendars',
										onClick: () => {
											navigate(
												`/calendar/${propertyId}/sync`
											);
										},
									},
								],
						}
					: undefined
				}
			/>
			<div className="portal-wrapper">
				<Spacer spacing={'large'} />
				<InlineChurnAlert />

				{showPropertySwitcher && (
					<>
						<PropertySwitcher
							customerId={customerId}
							switchProperty={setPropertyId}
							currentPropertyId={propertyId}
							loading={loading}
							properties={properties}
						/>
						<Spacer />
					</>
				)}
				<CalendarBundle
					propertyId={propertyId}
					loadingProperties={loading}
					initialMonth={persisted?.currentMonth || undefined}
					multiPropertyMode={showPropertySwitcher}
				/>
			</div>
		</div>
	);
};

export default CalendarPage;
